import { ajax_json_request } from "@/utils/api_utils";

const user_property_assignments_url = ensure_trailing_slash(process.env.VUE_APP_USER_PROPERTY_ASSIGNMENTS_URL);
const assign_properties_to_user_url = ensure_trailing_slash(process.env.VUE_APP_ASSIGN_PROPERTIES_TO_USER_URL);

function ensure_trailing_slash(url) {
  if (!url) {
    return "";
  }

  return url.endsWith("/") ? url : `${url}/`;
}

function build_url_with_query(base_url, query = {}) {
  const url = new URL(base_url, window.location.origin);

  Object.entries(query).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      url.searchParams.set(key, value);
    }
  });

  return url.toString();
}

function extract_assignment_collection(response_data) {
  if (Array.isArray(response_data)) {
    return response_data;
  }

  if (Array.isArray(response_data?.assignments)) {
    return response_data.assignments;
  }

  return [];
}

function get_assignment_property_id(assignment) {
  return get_related_assignment_id(assignment?.property);
}

function get_assignment_user_id(assignment) {
  return get_related_assignment_id(assignment?.user);
}

function get_related_assignment_id(entity) {
  if (entity && typeof entity === "object") {
    return entity.id ?? "";
  }

  return entity ?? "";
}

function get_assignment_property_name(assignment) {
  const property = assignment?.property;

  if (property && typeof property === "object") {
    return property.name || "";
  }

  return "";
}

function normalize_assignment(assignment) {
  return ({
    ...assignment,
    id: assignment?.id ?? "",
    user_id: get_assignment_user_id(assignment),
    property_id: get_assignment_property_id(assignment),
    property_name: get_assignment_property_name(assignment),
    is_active: assignment?.is_active !== false
  });
}

export function get_user_property_assignments_url() {
  return user_property_assignments_url;
}

export async function fetch_user_property_assignments(user_id, headers, { include_inactive = true } = {}) {
  if (!user_property_assignments_url || !user_id) {
    return [];
  }

  const request_url = build_url_with_query(user_property_assignments_url, {
    user_id: user_id,
    ...(include_inactive ? { active_only: "false" } : { active_only: "true" })
  });
  const response_data = await fetch_user_property_assignment_json(request_url, {
    method: "GET",
    headers
  });

  return extract_assignment_collection(response_data).map((assignment) => normalize_assignment(assignment));
}

export function get_assignment_property_ids(assignments, { active_only = true } = {}) {
  return assignments
    .filter((assignment) => !active_only || assignment.is_active)
    .map((assignment) => assignment.property_id)
    .filter((property_id) => property_id !== null && property_id !== undefined && property_id !== "")
    .map((property_id) => String(property_id))
    .filter((property_id, index, all_ids) => all_ids.indexOf(property_id) === index);
}

export function get_assignment_properties(assignments, property_options = [], { active_only = true } = {}) {
  return get_assignment_property_ids(assignments, { active_only })
    .map((property_id) => {
      const matching_property = property_options.find((property) => String(property.id) === String(property_id));
      const matching_assignment = assignments.find((assignment) => String(assignment.property_id) === String(property_id));

      return matching_property || {
        id: property_id,
        name: matching_assignment?.property_name || String(property_id)
      };
    });
}

export async function sync_user_property_assignments(user_id, selected_property_ids, headers) {
  if (!user_property_assignments_url) {
    throw new Error("VUE_APP_USER_PROPERTY_ASSIGNMENTS_URL is not configured.");
  }

  if (!user_id) {
    throw new Error("A user id is required before property assignments can be saved.");
  }

  const next_property_ids = (selected_property_ids || [])
    .map((property_id) => String(property_id))
    .filter(Boolean);
  const existing_assignments = await fetch_user_property_assignments(user_id, headers, { include_inactive: true });
  const requests = [];

  if (next_property_ids.length) {
    requests.push(assign_properties_to_user(user_id, next_property_ids, headers));
  }

  existing_assignments
    .filter((assignment) => assignment.is_active && !next_property_ids.includes(String(assignment.property_id)))
    .forEach((assignment) => {
      if (assignment.id) {
        requests.push(update_user_property_assignment(assignment.id, { is_active: false }, headers));
      }
    });

  await Promise.all(requests);

  return fetch_user_property_assignments(user_id, headers, { include_inactive: true });
}

async function assign_properties_to_user(user_id, property_ids, headers) {
  if (!assign_properties_to_user_url) {
    throw new Error("VUE_APP_ASSIGN_PROPERTIES_TO_USER_URL is not configured.");
  }

  const serializer_payload = {
    user_id: Number(user_id),
    property_ids: property_ids.map((property_id) => Number(property_id)),
    is_active: true
  };
  const model_payload = {
    user: Number(user_id),
    properties: property_ids.map((property_id) => Number(property_id)),
    is_active: true
  };

  try {
    return await post_assign_properties_to_user(serializer_payload, headers);
  } catch (error) {
    return post_assign_properties_to_user(model_payload, headers);
  }
}

async function post_assign_properties_to_user(payload, headers) {
  return fetch_user_property_assignment_json(assign_properties_to_user_url, {
    method: "POST",
    headers,
    body: JSON.stringify(payload)
  });
}

async function update_user_property_assignment(assignment_id, payload, headers) {
  return fetch_user_property_assignment_json(`${user_property_assignments_url}${assignment_id}/update/`, {
    method: "POST",
    headers,
    body: JSON.stringify(payload)
  });
}

function fetch_user_property_assignment_json(request_url, options = {}) {
  if (!request_url) {
    throw new Error("User property assignment endpoint is not configured.");
  }

  return ajax_json_request(request_url, options);
}
